// hooks/usePermissions.js - Complete Fixed Version with All Enhancements
// 🔥 PRODUCTION FIX: branch_operator permissions BYPASS branch selection

import { useAppSelector } from './redux';
import { useMemo, useCallback } from 'react';

//  ADDED: Debug mode control - only logs when explicitly enabled
const DEBUG_PERMISSIONS = process.env.NODE_ENV === 'development' &&
(localStorage.getItem('debugPermissions') === 'true' || window.DEBUG_PERMISSIONS);

//  ADDED: Centralized debug logger to reduce repeated logs
const debugLog = (message, data = null) => {
  if (DEBUG_PERMISSIONS) {
    if (data) {
      console.log(`[Auth] [Permissions] ${message}:`, data);
    } else {
      console.log(`[Auth] [Permissions] ${message}`);
    }
  }
};

//  ADDED: Cache for permission calculations to reduce repeated computations
const permissionCache = new Map();
const CACHE_DURATION = 5000; // 5 seconds cache

const getCacheKey = (user, branch, permission) => {
  return `${user?.username}_${user?.role}_${branch?._id}_${permission}`;
};

export const usePermissions = () => {
  const {
    user,
    selectedBranch,
    permissions,
    loading,
    branchSelectionRequired,
    availableBranches
  } = useAppSelector((state) => state.auth);

  // 🔥 PRODUCTION FIX: Main permission checker - branch_operator BYPASS
  const hasPermission = useCallback((permission) => {
    // Return false during loading to prevent premature access
    if (loading) return false;

    if (!user) {
      //  REDUCED: Only log once per session for missing user
      if (!window.__permissionUserWarningShown) {
        debugLog('No user for permission check', permission);
        window.__permissionUserWarningShown = true;
      }
      return false;
    }

    // CRITICAL FIX: branchoperator permissions (WITHOUT logs.read)
    if (user.role === 'branchoperator') {
      const operatorPermissions = [
        'rooms.read', 'rooms.update',
        'vouchers.read', 'vouchers.update'
        // NOTE: logs.read intentionally EXCLUDED - branch operators cannot view logs
      ];
      console.log('🔧 BRANCHOPERATOR permission check:', permission, '→', operatorPermissions.includes(permission));
      return operatorPermissions.includes(permission);
    }

    //  ADDED: Check cache first
    const cacheKey = getCacheKey(user, selectedBranch, permission);
    const cached = permissionCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
      return cached.result;
    }

    let hasIt = false;

    //  CRITICAL: Super admin ALWAYS has ALL permissions (except token-based sessions)
    if (user.role === 'super_admin' && !user.isTokenUser) {
      hasIt = true;
      //  REDUCED: Only log once per permission type for super admin
      if (!window.__superAdminPermissionLogs) window.__superAdminPermissionLogs = new Set();
      if (!window.__superAdminPermissionLogs.has(permission)) {
        debugLog('Super admin permission granted', permission);
        window.__superAdminPermissionLogs.add(permission);
      }
    }
    //  CRITICAL: Admin has most permissions (no branch required)
    else if (user.role === 'admin' && !user.isTokenUser) {
      // Only deny admin access to super admin exclusive permissions
      const superAdminOnlyPermissions = [
        'users.delete', // Can't delete users
        'branches.delete', // Can't delete branches
        'settings.delete' // Can't delete system settings
      ];
      if (superAdminOnlyPermissions.includes(permission)) {
        hasIt = false;
        //  REDUCED: Only log once per denied permission for admin
        if (!window.__adminDeniedLogs) window.__adminDeniedLogs = new Set();
        if (!window.__adminDeniedLogs.has(permission)) {
          debugLog('Admin denied super admin only permission', permission);
          window.__adminDeniedLogs.add(permission);
        }
      } else {
        hasIt = true;
        //  REDUCED: Only log first few admin permissions
        if (!window.__adminPermissionCount) window.__adminPermissionCount = 0;
        if (window.__adminPermissionCount < 5) {
          debugLog('Admin permission granted', permission);
          window.__adminPermissionCount++;
        }
      }
    }
    else {
      if (!permissions || !Array.isArray(permissions)) {
        //  REDUCED: Only log once per user for missing permissions
        if (!window.__permissionArrayWarnings) window.__permissionArrayWarnings = new Set();
        if (!window.__permissionArrayWarnings.has(user.username)) {
          debugLog('No permissions array for user', user.username);
          window.__permissionArrayWarnings.add(user.username);
        }
        hasIt = false;
      } else {
        //  NEW: For branch-dependent roles, check branch selection
        if (['branch_manager', 'branchoperator'].includes(user.role)) {
          // If system requires branch selection but none is selected
          if (branchSelectionRequired && !selectedBranch) {
            //  REDUCED: Only log once per session for branch selection requirement
            if (!window.__branchSelectionWarningShown) {
              debugLog('Branch selection required but not selected', `${user.role} - ${permission}`);
              window.__branchSelectionWarningShown = true;
            }
            hasIt = false;
          } else if (selectedBranch && !checkBranchAccess(user, selectedBranch)) {
            //  REDUCED: Only log once per branch for access denial
            if (!window.__branchAccessWarnings) window.__branchAccessWarnings = new Set();
            const branchKey = `${user.username}_${selectedBranch._id}`;
            if (!window.__branchAccessWarnings.has(branchKey)) {
              debugLog('User has no access to selected branch', `${user.username} - ${selectedBranch.name}`);
              window.__branchAccessWarnings.add(branchKey);
            }
            hasIt = false;
          } else {
            hasIt = permissions.includes(permission);
          }
        } else {
          // Check specific permission
          hasIt = permissions.includes(permission);
        }
      }
    }

    //  REDUCED: Only log permission checks in debug mode and limit frequency
    if (DEBUG_PERMISSIONS && Math.random() < 0.1) { // Only 10% of permission checks logged
      debugLog(`Permission check for ${user.username}`, `${permission} = ${hasIt}`);
    }

    //  ADDED: Cache the result
    permissionCache.set(cacheKey, {
      result: hasIt,
      timestamp: Date.now()
    });

    //  ADDED: Clean cache periodically
    if (permissionCache.size > 100) {
      const now = Date.now();
      for (const [key, value] of permissionCache.entries()) {
        if (now - value.timestamp > CACHE_DURATION) {
          permissionCache.delete(key);
        }
      }
    }

    return hasIt;
  }, [user, permissions, selectedBranch, loading, branchSelectionRequired]);

  //  NEW: Helper function to check branch access
  const checkBranchAccess = useCallback((user, branch) => {
    if (!user || !branch) return false;

    // Super admin and admin have access to all branches
    if (['super_admin', 'admin'].includes(user.role)) return true;

    // Check primary branch
    if (user.primaryBranch) {
      const primaryBranchId = typeof user.primaryBranch === 'object'
        ? user.primaryBranch._id
        : user.primaryBranch;
      if (primaryBranchId === branch._id) return true;
    }

    // Check branches array
    if (user.branches && Array.isArray(user.branches)) {
      return user.branches.some(b => {
        const branchId = typeof b.branch === 'object' ? b.branch._id : b.branch;
        return branchId === branch._id;
      });
    }

    return false;
  }, []);

  //  ENHANCED: Check if user can access data (permission + branch validation)
  const canAccessData = useCallback((permission) => {
    if (!hasPermission(permission)) return false;

    // For roles that require branch selection
    if (['branch_manager', 'branch_operator'].includes(user?.role)) {
      if (branchSelectionRequired && !selectedBranch) return false;
    }

    return true;
  }, [hasPermission, branchSelectionRequired, selectedBranch, user?.role]);

  //  ENHANCED: Multiple permission checks
  const hasAnyPermission = useCallback((permissionList) => {
    if (!Array.isArray(permissionList)) return false;
    return permissionList.some(permission => hasPermission(permission));
  }, [hasPermission]);

  const hasAllPermissions = useCallback((permissionList) => {
    if (!Array.isArray(permissionList)) return false;
    return permissionList.every(permission => hasPermission(permission));
  }, [hasPermission]);

  //  ENHANCED: Specific management permissions with super admin bypass (minimal logging)
  const canManageUsers = useMemo(() => {
    if (loading) return false;
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'admin') return true;
    if (user?.role === 'branch_manager') {
      return hasPermission('users.create') || hasPermission('users.update');
    }
    return false;
  }, [hasPermission, user?.role, loading]);

  const canManageBranches = useMemo(() => {
    if (loading) return false;
    // Only super admin can manage branches
    return user?.role === 'super_admin';
  }, [user?.role, loading]);

  const canManageDevices = useMemo(() => {
    if (loading) return false;
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'admin') return true;
    if (user?.role === 'branch_manager') return true;
    return hasPermission('devices.create') || hasPermission('devices.update');
  }, [hasPermission, user?.role, loading]);

  const canManageVouchers = useMemo(() => {
    if (loading) return false;
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'admin') return true;
    return hasPermission('vouchers.create') || hasPermission('vouchers.update');
  }, [hasPermission, user?.role, loading]);

  const canManageRooms = useMemo(() => {
    if (loading) return false;
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'admin') return true;
    return hasPermission('rooms.create') || hasPermission('rooms.update');
  }, [hasPermission, user?.role, loading]);

  const canViewLogs = useMemo(() => {
    if (loading) return false;
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'admin') return true;
    if (user?.role === 'branch_manager') return true;
    return hasPermission('logs.read');
  }, [hasPermission, user?.role, loading]);

  const canAccessMonitoring = useMemo(() => {
    if (loading) return false;
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'admin') return true;
    if (user?.role === 'branch_manager') return true;
    return hasPermission('monitoring.read');
  }, [hasPermission, user?.role, loading]);

  const canAccessSettings = useMemo(() => {
    if (loading) return false;
    if (user?.role === 'super_admin') return true;
    if (user?.role === 'admin') return hasPermission('settings.read');
    return false;
  }, [hasPermission, user?.role, loading]);

  //  ENHANCED: Branch-filtered data with better logic (minimal logging)
  const getBranchFilteredData = useCallback((data) => {
    if (!Array.isArray(data)) return data;

    // During loading, return empty array for safety
    if (loading) return [];

    // Super admin sees all data regardless of branch selection
    if (user?.role === 'super_admin') return data;

    // Admin sees all data but can filter by branch if one is selected
    if (user?.role === 'admin') {
      if (!selectedBranch) return data; // Show all if no branch selected

      // Filter by selected branch
      return data.filter(item => {
        if (item.branch) {
          const itemBranchId = typeof item.branch === 'object' ? item.branch._id : item.branch;
          return itemBranchId === selectedBranch._id;
        }
        if (item.branchId) {
          return item.branchId === selectedBranch._id;
        }
        return false;
      });
    }

    // For branch-dependent roles, filter by selected branch
    if (['branch_manager', 'branch_operator'].includes(user?.role)) {
      if (!selectedBranch) return []; // Must have branch selected
      return data.filter(item => {
        if (item.branch) {
          const itemBranchId = typeof item.branch === 'object' ? item.branch._id : item.branch;
          return itemBranchId === selectedBranch._id;
        }
        if (item.branchId) {
          return item.branchId === selectedBranch._id;
        }
        // Also check if user has access to item's branch
        if (item.branchId && user.branches) {
          return user.branches.some(ub => {
            const userBranchId = typeof ub.branch === 'object' ? ub.branch._id : ub.branch;
            return userBranchId === item.branchId;
          });
        }
        return false;
      });
    }

    return [];
  }, [selectedBranch, user, loading]);

  //  NEW: Check if current user requires branch selection
  const requiresBranchSelection = useMemo(() => {
    if (!user) return false;
    return !['super_admin', 'admin'].includes(user.role);
  }, [user?.role]);

  //  NEW: Get user's effective permissions based on role (minimal logging)
  const getEffectivePermissions = useMemo(() => {
    if (!user) return [];

    if (user.role === 'super_admin') {
      //  REDUCED: Only log once for super admin effective permissions
      if (!window.__superAdminEffectiveLogged) {
        debugLog('Super admin effective permissions calculated');
        window.__superAdminEffectiveLogged = true;
      }
      return [
        'branches.create', 'branches.read', 'branches.update', 'branches.delete',
        'users.create', 'users.read', 'users.update', 'users.delete',
        'devices.create', 'devices.read', 'devices.update', 'devices.delete',
        'rooms.create', 'rooms.read', 'rooms.update', 'rooms.delete',
        'vouchers.create', 'vouchers.read', 'vouchers.update', 'vouchers.delete',
        'logs.read', 'logs.delete',
        'monitoring.read', 'monitoring.emergency_codes',
        'settings.read', 'settings.update', 'settings.delete'
      ];
    }

    if (user.role === 'admin') {
      return [
        'branches.read',
        'users.create', 'users.read', 'users.update',
        'devices.create', 'devices.read', 'devices.update', 'devices.delete',
        'rooms.create', 'rooms.read', 'rooms.update', 'rooms.delete',
        'vouchers.create', 'vouchers.read', 'vouchers.update', 'vouchers.delete',
        'logs.read',
        'monitoring.read', 'monitoring.emergency_codes',
        'settings.read', 'settings.update'
      ];
    }

    if (user.role === 'branch_manager') {
      return [
        'users.create', 'users.read', 'users.update',
        'devices.read', 'devices.update',
        'rooms.read', 'rooms.update',
        'vouchers.create', 'vouchers.read', 'vouchers.update', 'vouchers.delete',
        'logs.read',
        'monitoring.read',
        'settings.read'
      ];
    }

    if (user.role === 'branch_operator') {
      return [
        'devices.read',
        'rooms.read', 'rooms.update',
        'vouchers.read', 'vouchers.update',
        'logs.read'
      ];
    }

    return permissions || [];
  }, [user, permissions]);

  //  NEW: Get accessible branches for current user
  const getAccessibleBranches = useMemo(() => {
    if (!user || !availableBranches) return [];

    // Super admin and admin can access all branches
    if (['super_admin', 'admin'].includes(user.role)) {
      return availableBranches;
    }

    // Filter branches based on user access
    return availableBranches.filter(branch => checkBranchAccess(user, branch));
  }, [user, availableBranches, checkBranchAccess]);

  //  NEW: Check if user is in a specific role
  const isInRole = useCallback((role) => {
    return user?.role === role;
  }, [user?.role]);

  //  NEW: Check if user is admin level (admin or super_admin)
  const isAdminLevel = useMemo(() => {
    return ['super_admin', 'admin'].includes(user?.role);
  }, [user?.role]);

  //  NEW: Check if user is manager level (branch_manager or higher)
  const isManagerLevel = useMemo(() => {
    return ['super_admin', 'admin', 'branch_manager'].includes(user?.role);
  }, [user?.role]);

  //  NEW: Validation helpers
  const validateBranchAccess = useCallback((action, targetBranchId = null) => {
    if (!hasPermission(action)) return false;

    // Use selected branch if no target specified
    const branchId = targetBranchId || selectedBranch?._id;
    if (!branchId) return isAdminLevel; // Admin can work without branch selection

    // Check if user has access to the target branch
    if (['branch_manager', 'branch_operator'].includes(user?.role)) {
      const targetBranch = availableBranches?.find(b => b._id === branchId);
      return targetBranch ? checkBranchAccess(user, targetBranch) : false;
    }

    return true; // Admin level users can access any branch
  }, [hasPermission, selectedBranch, isAdminLevel, user?.role, availableBranches, checkBranchAccess]);

  //  ADDED: Debug utilities
  const debugUtils = {
    enableDebugMode: () => {
      localStorage.setItem('debugPermissions', 'true');
      window.DEBUG_PERMISSIONS = true;
      console.log(' Permission debugging enabled');
    },
    disableDebugMode: () => {
      localStorage.removeItem('debugPermissions');
      window.DEBUG_PERMISSIONS = false;
      console.log(' Permission debugging disabled');
    },
    clearPermissionCache: () => {
      permissionCache.clear();
      console.log(' Permission cache cleared');
    },
    getPermissionCacheStats: () => {
      return {
        size: permissionCache.size,
        entries: Array.from(permissionCache.keys())
      };
    }
  };

  return {
    // Core permission functions
    hasPermission,
    canAccessData,
    hasAnyPermission,
    hasAllPermissions,
    // Specific management permissions
    canManageUsers,
    canManageBranches,
    canManageDevices,
    canManageVouchers,
    canManageRooms,
    canViewLogs,
    canAccessMonitoring,
    canAccessSettings,
    // Data filtering and branch management
    getBranchFilteredData,
    getAccessibleBranches,
    checkBranchAccess,
    validateBranchAccess,
    // Role checking utilities
    isInRole,
    isAdminLevel,
    isManagerLevel,
    requiresBranchSelection,
    getEffectivePermissions,
    // State information
    selectedBranch,
    branchSelectionRequired,
    permissions,
    user,
    loading,
    availableBranches,
    //  ADDED: Debug utilities (only in development)
    ...(process.env.NODE_ENV === 'development' && { debugUtils })
  };
};

//  CRITICAL FIX: Add default export to resolve build error
export default usePermissions;
